import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Search, Plus, Phone, Mail, ChevronRight, Star, DollarSign, Calendar, Download } from 'lucide-react';
import { formatFullName, formatPhone, formatCurrency, searchFilter } from '../utils/helpers';
import { employeeRoles } from '../utils/sampleData';
import Modal from '../components/Modal';
import EmployeeForm from '../components/EmployeeForm';
import TimeOffManager from '../components/TimeOffManager';
import '../components/TimeOffManager.css';
import './Employees.css';

function Employees() {
  const { state, addEmployee, updateEmployee } = useApp();
  const { employees } = state;
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterRole, setFilterRole] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showTimeOffManager, setShowTimeOffManager] = useState(false);
  
  // Get pending time-off requests count
  const pendingTimeOffCount = state.timeOffRequests?.filter(r => r.status === 'pending').length || 0;

  const filteredEmployees = useMemo(() => {
    let result = employees;
    
    if (filterStatus !== 'all') {
      // "Active" includes employees still in onboarding so managers can see
      // their full roster — onboarding employees are real, working staff
      // who just haven't finished their checklist yet.
      result = result.filter(e =>
        filterStatus === 'active'
          ? (e.status === 'active' || e.status === 'onboarding')
          : e.status === filterStatus
      );
    }
    
    if (filterRole !== 'all') {
      result = result.filter(e => e.role === filterRole);
    }
    
    if (searchTerm) {
      result = searchFilter(result, searchTerm, [
        'firstName', 'lastName', 'email', 'phone'
      ]);
    }
    
    return result;
  }, [employees, searchTerm, filterStatus, filterRole]);

  const handleAddEmployee = () => {
    console.log('handleAddEmployee called, setting showModal to true');
    setEditingEmployee(null);
    setShowModal(true);
  };

  const exportEmployeesCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Role', 'Hourly Rate', 'Status', 'Hire Date'];
    const rows = filteredEmployees.map(e => [
      e.firstName || '',
      e.lastName || '',
      e.email || '',
      e.phone || '',
      e.role === 'lead' ? 'Team Lead' : 'Cleaner',
      e.hourlyRate || '',
      e.status || '',
      e.hireDate || ''
    ]);
    const escape = (val) => {
      const s = String(val);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employees-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleEditEmployee = (e, employee) => {
    e.preventDefault();
    setEditingEmployee(employee);
    setShowModal(true);
  };

  const handleSaveEmployee = async (employeeData) => {
    if (editingEmployee) {
      const saved = await updateEmployee(editingEmployee.id, employeeData);
      if (saved === false) {
        alert("Couldn't save changes to this employee. Please check your connection and try again.");
        return;
      }
    } else {
      const result = await addEmployee({
        ...employeeData,
        skills: []
      });
      if (result?._loginError) {
        alert(`Employee saved, but login account issue: ${result._loginError}`);
      } else if (result?._loginCreated) {
        alert(`Employee added and login account created! Share the temporary password with ${result.firstName} so they can log in.`);
      }
    }
    setShowModal(false);
    setEditingEmployee(null);
  };

  return (
    <div className="employees-page">
      <div className="page-header">
        <div>
          <h1>Employees</h1>
          <p className="header-subtitle">
            {filteredEmployees.length} {filteredEmployees.length === 1 ? 'employee' : 'employees'}
          </p>
        </div>
        <div className="header-actions">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={exportEmployeesCSV}
            title="Export to CSV"
          >
            <Download size={18} />
            Export CSV
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => setShowTimeOffManager(true)}
          >
            <Calendar size={18} />
            Time Off
            {pendingTimeOffCount > 0 && (
              <span className="badge">{pendingTimeOffCount}</span>
            )}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleAddEmployee}>
            <Plus size={18} />
            Add Employee
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <select 
          value={filterRole} 
          onChange={(e) => setFilterRole(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Roles</option>
          <option value="lead">Team Lead</option>
          <option value="employee">Cleaner</option>
        </select>
        
        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="onboarding">Onboarding</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="employees-list">
        {filteredEmployees.length === 0 ? (
          <div className="empty-state">
            <p>No employees found</p>
            <button type="button" className="btn btn-primary" onClick={handleAddEmployee}>
              <Plus size={18} />
              Add Your First Employee
            </button>
          </div>
        ) : (
          filteredEmployees.map(employee => {
            const roleConfig = employeeRoles[employee.role];
            
            return (
              <Link key={employee.id} to={`/admin/employees/${employee.id}`} className="employee-card">
                <div 
                  className="employee-avatar"
                  style={{ background: employee.role === 'lead' ? 'var(--gold)' : 'var(--sage)' }}
                >
                  {employee.avatar}
                </div>
                
                <div className="employee-info">
                  <div className="employee-name">
                    {formatFullName(employee)}
                    {employee.role === 'lead' && <Star size={14} className="lead-star" />}
                    <span className={`employee-status ${employee.status}`}>
                      {employee.status}
                    </span>
                  </div>
                  <div className="employee-role">
                    {roleConfig?.label || employee.role}
                  </div>
                  <div className="employee-contact">
                    <span className="contact-item">
                      <Phone size={14} />
                      {formatPhone(employee.phone)}
                    </span>
                    <span className="contact-item">
                      <Mail size={14} />
                      {employee.email}
                    </span>
                  </div>
                </div>
                
                <div className="employee-pay">
                  <DollarSign size={14} />
                  {employee.payRates?.length > 0
                    ? employee.payRates.map(pr => `${pr.label}: $${pr.rate.toFixed(2)}`).join(' | ')
                    : `${formatCurrency(employee.hourlyRate)}/hr`
                  }
                </div>
                
                <div className="employee-actions">
                  <button 
                    className="edit-btn"
                    onClick={(e) => handleEditEmployee(e, employee)}
                  >
                    Edit
                  </button>
                  <ChevronRight size={20} className="chevron" />
                </div>
              </Link>
            );
          })
        )}
      </div>

      {showModal && (
        <Modal
          isOpen={showModal}
          title={editingEmployee ? 'Edit Employee' : 'Add New Employee'}
          onClose={() => { setShowModal(false); setEditingEmployee(null); }}
        >
          <EmployeeForm
            employee={editingEmployee}
            onSave={handleSaveEmployee}
            onCancel={() => { setShowModal(false); setEditingEmployee(null); }}
          />
        </Modal>
      )}

      {/* Time Off Manager Modal */}
      <TimeOffManager
        isOpen={showTimeOffManager}
        onClose={() => setShowTimeOffManager(false)}
      />
    </div>
  );
}

export default Employees;
